// Web-build login gate — authenticates users against stenal.net (Bubble.io)
// through the secrets server, so only authorized users can use the app.
//
// The flow: a stored session token is validated via GET /api/auth/me; if there
// is none (or it is rejected) a full-screen login overlay collects email +
// password and POSTs /api/auth/login. The server returns { token, expires, user }
// where user = { id, stenalcad, commerciale, agente } and only `stenalcad`
// admits the user. `ensureAuthenticated()` resolves once a valid session exists,
// and web.ts awaits it BEFORE importing the renderer, so an unauthenticated user
// never reaches boot().

import {
  tr,
  trMessage,
} from './i18n';
import {
  clearSession,
  getSessionToken,
  SERVER_BASE,
  setReauthHandler,
} from './webPlatform';

export interface AuthUser {
  id: string;
  /** The user's display name (Bubble `nome`); '' when not provided. */
  nome: string;
  stenalcad: boolean;
  /** May the user save the shared standards (catalogue / benches / colours /
   *  title blocks)? False ⇒ read-only. */
  edit: boolean;
  commerciale: boolean;
  agente: boolean;
  /** Unique id of the user's area manager ('' / absent when none). Set by the
   *  server from Bubble; when present, projects the user creates are auto-shared
   *  with this manager (enforced server-side). */
  areamanager?: string;
}
interface Session { token: string; expires: number; user: AuthUser; savedAt: number }

const SESSION_KEY = 'stenal.session';

function readSession(): Session | null {
  try { const s = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null') as Session | null; return s && s.token ? s : null; }
  catch { return null; }
}
function writeSession(token: string, expires: number, user: AuthUser): void {
  try { localStorage.setItem(SESSION_KEY, JSON.stringify({ token, expires, user, savedAt: Date.now() } satisfies Session)); }
  catch { /* private mode */ }
}

/** The signed-in user's claims (for future `commerciale` / `agente` features). */
export function getUser(): AuthUser | null { return readSession()?.user ?? null; }

async function serverJson(path: string, init: RequestInit = {}): Promise<{ status: number; body: Record<string, unknown> }> {
  const res = await fetch(`${SERVER_BASE}${path}`, init);
  const text = await res.text().catch(() => '');
  let body: Record<string, unknown> = {};
  try { body = text ? (JSON.parse(text) as Record<string, unknown>) : {}; } catch { /* non-JSON */ }
  return { status: res.status, body };
}

// ---- login overlay ----
const CSS = `
.stenal-auth { position:fixed; inset:0; z-index:100000; display:flex; align-items:center; justify-content:center;
  background:#1c1c1c; font:14px system-ui,Segoe UI,sans-serif; color:#eee; }
.stenal-auth-card { width:340px; max-width:88vw; background:#262626; border:1px solid #111; border-radius:10px;
  box-shadow:0 12px 40px rgba(0,0,0,.5); padding:26px 26px 22px; }
.stenal-auth-brand { font-size:22px; font-weight:700; margin-bottom:4px; }
.stenal-auth-brand span { color:#4aa3ff; }
.stenal-auth-sub { color:#9a9a9a; font-size:12.5px; margin-bottom:18px; }
.stenal-auth label { display:block; font-size:12px; color:#bdbdbd; margin:12px 0 4px; }
.stenal-auth input[type=email], .stenal-auth input[type=password] { width:100%; height:auto; box-sizing:border-box; padding:9px 10px;
  background:#1c1c1c; border:1px solid #444; border-radius:6px; color:#eee; font:inherit; }
.stenal-auth input:focus { outline:none; border-color:#4aa3ff; }
.stenal-auth-remember { display:flex; align-items:center; gap:8px; margin:14px 0 2px; color:#bdbdbd; font-size:12.5px; }
.stenal-auth-remember input { margin:0; }
.stenal-auth-btn { width:100%; margin-top:16px; padding:10px; border:0; border-radius:6px; background:#3d7eff; color:#fff;
  font:inherit; font-weight:600; cursor:pointer; }
.stenal-auth-btn:disabled { opacity:.6; cursor:default; }
.stenal-auth-err { min-height:1.1em; margin-top:12px; color:#ff6b6b; font-size:12.5px; }
`;

function ensureStyle(): void {
  if (document.getElementById('stenal-auth-style')) return;
  const s = document.createElement('style');
  s.id = 'stenal-auth-style';
  s.textContent = CSS;
  document.head.appendChild(s);
}

/** Show the login overlay and resolve once a valid session is stored. */
function showLogin(): Promise<void> {
  ensureStyle();
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'stenal-auth';
    overlay.innerHTML = `
      <form class="stenal-auth-card" autocomplete="on">
        <div class="stenal-auth-brand">Stenal<span>CAD</span></div>
        <div class="stenal-auth-sub">Sign in with your stenal.net account.</div>
        <label for="stenal-auth-email">Email</label>
        <input id="stenal-auth-email" type="email" autocomplete="username" required />
        <label for="stenal-auth-pass">Password</label>
        <input id="stenal-auth-pass" type="password" autocomplete="current-password" required />
        <label class="stenal-auth-remember"><input id="stenal-auth-remember" type="checkbox" checked /> Keep me signed in</label>
        <button class="stenal-auth-btn" type="submit">Sign in</button>
        <div class="stenal-auth-err" id="stenal-auth-err"></div>
      </form>`;
    document.body.appendChild(overlay);
    document.getElementById('boot-loading')?.remove(); // the sign-in form replaces the boot screen
    const form = overlay.querySelector('form') as HTMLFormElement;
    const email = overlay.querySelector('#stenal-auth-email') as HTMLInputElement;
    const pass = overlay.querySelector('#stenal-auth-pass') as HTMLInputElement;
    const remember = overlay.querySelector('#stenal-auth-remember') as HTMLInputElement;
    const btn = overlay.querySelector('.stenal-auth-btn') as HTMLButtonElement;
    const err = overlay.querySelector('#stenal-auth-err') as HTMLDivElement;
    email.focus();

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      err.textContent = '';
      btn.disabled = true; btn.textContent = tr('Signing in…');
      try {
        const { status, body } = await serverJson('/api/auth/login', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.value.trim(), password: pass.value, remember: remember.checked }),
        });
        if (status === 200 && typeof body.token === 'string') {
          writeSession(body.token, Number(body.expires) || 86400, body.user as AuthUser);
          overlay.remove();
          resolve();
          return;
        }
        err.textContent = typeof body.error === 'string' && body.error
          ? trMessage(body.error)
          : status === 401 ? tr('Invalid email or password.')
          : status === 403 ? tr('This account is not authorized to use StenalCAD.')
          : tr('Sign-in failed. Please try again.');
      } catch {
        err.textContent = tr('Could not reach the server. Check your connection.');
      } finally {
        btn.disabled = false; btn.textContent = tr('Sign in');
      }
    });
  });
}

let inFlight: Promise<void> | null = null;
/** Show the login overlay (deduped: concurrent 401s share one overlay). */
function requireLogin(): Promise<void> {
  if (!inFlight) inFlight = showLogin().finally(() => { inFlight = null; });
  return inFlight;
}

/** Resolve once a valid, authorized session exists — prompting for login if not.
 *  Called by web.ts before the renderer boots, and as the 401 re-auth handler. */
export async function ensureAuthenticated(): Promise<void> {
  setReauthHandler(requireLogin);
  if (getSessionToken()) {
    // Resume: let the server validate the stored token (and re-check the role).
    try {
      const { status, body } = await serverJson('/api/auth/me', { headers: { Authorization: `Bearer ${getSessionToken()}` } });
      if (status === 200 && body.user) {
        const s = readSession();
        if (s) writeSession(s.token, s.expires, body.user as AuthUser); // refresh claims
        return;
      }
    } catch { /* server unreachable → fall through to the login form */ }
    clearSession();
  }
  await requireLogin();
}

export type PermissionRefresh =
  | { status: 'updated'; user: AuthUser; changed: boolean } // re-checked OK (claims may have changed)
  | { status: 'revoked' }                                   // no longer authorized → must sign in again
  | { status: 'offline' };                                  // couldn't reach the server

/** Re-check the signed-in user's claims with stenal.net (GET /api/auth/me) WITHOUT
 *  a re-login, and update the stored session in place. Lets a permission change on
 *  stenal.net take effect without signing out. */
export async function refreshPermissions(): Promise<PermissionRefresh> {
  const token = getSessionToken();
  if (!token) return { status: 'revoked' };
  let status: number; let body: Record<string, unknown>;
  try {
    ({ status, body } = await serverJson('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } }));
  } catch { return { status: 'offline' }; }
  if (status === 200 && body.user) {
    const before = getUser();
    const user = body.user as AuthUser;
    const s = readSession();
    if (s) writeSession(s.token, s.expires, user); // update claims, keep the token
    return { status: 'updated', user, changed: JSON.stringify(before) !== JSON.stringify(user) };
  }
  if (status === 401 || status === 403) { clearSession(); return { status: 'revoked' }; } // access pulled
  return { status: 'offline' };
}

/** Clear the session (server + browser) and reload to the login gate. */
export async function logout(): Promise<void> {
  const token = getSessionToken();
  if (token) { try { await fetch(`${SERVER_BASE}/api/auth/logout`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }); } catch { /* ignore */ } }
  clearSession();
  location.reload();
}
